import React from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { HeroLogo } from './HeroLogo';
import { NavBarLogo } from './NavBarLogo';

// OS Logo Components
const AppleLogo = () => (
  <svg
    className="w-6 h-6 apple-logo"
    viewBox="0 0 24 24"
    fill="currentColor"
    width="24"
    height="24"
  >
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const AndroidLogo = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
    <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-2.86-1.21-6.08-1.21-8.94 0L5.65 5.67c-.19-.29-.51-.38-.83-.22-.31.16-.43.53-.27.85L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z" />
  </svg>
);

const WindowsLogo = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
    <path d="M3 5.5L10.5 4.5V11.5H3V5.5ZM10.5 12.5V19.5L3 18.5V12.5H10.5ZM11.5 4.35L21 3V11.5H11.5V4.35ZM21 12.5V21L11.5 19.65V12.5H21Z" />
  </svg>
);

const LinuxLogo = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
    <path d="M12 5C10.89 5 10 5.89 10 7V9.27C9.38 9.63 9 10.29 9 11V14C9 14.55 9.45 15 10 15H11V19C11 19.55 11.45 20 12 20S13 19.55 13 19V15H14C14.55 15 15 14.55 15 14V11C15 10.29 14.62 9.63 14 9.27V7C14 5.89 13.11 5 12 5M11 7C11 6.45 11.45 6 12 6S13 6.45 13 7V9H11V7Z" />
  </svg>
);

// Platform-specific download button component
const PlatformDownloadButton = ({
  platform,
  size = 'large',
}: {
  platform: string;
  size?: 'small' | 'large';
}) => {
  const btnClass =
    size === 'small'
      ? 'inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-full transition-colors text-sm nav-download-btn'
      : 'inline-flex items-center gap-3 px-8 py-4 bg-primary hover:bg-primary-dark text-white rounded-full transition-all transform hover:scale-105 text-lg font-semibold shadow-lg';
  // Mobile platforms
  if (platform === 'ios') {
    return (
      <a
        href="https://apps.apple.com/app/soulthread"
        className={btnClass}
        target="_blank"
        rel="noopener noreferrer"
      >
        <>
          <AppleLogo />
          <span className="btn-text-full">
            {size === 'small' ? 'App Store' : 'Download on the App Store'}
          </span>
          <span className="btn-text-mobile">Download</span>
        </>
      </a>
    );
  }

  if (platform === 'android') {
    return (
      <a
        href="https://play.google.com/store/apps/details?id=com.soulthread"
        className={btnClass}
        target="_blank"
        rel="noopener noreferrer"
      >
        <>
          <AndroidLogo />
          <span className="btn-text-full">
            {size === 'small' ? 'Google Play' : 'Get it on Google Play'}
          </span>
          <span className="btn-text-mobile">Download</span>
        </>
      </a>
    );
  }

  // Desktop platforms
  if (platform === 'mac') {
    return (
      <a
        href="/downloads/SoulThread-mac.dmg"
        className={btnClass}
        target="_blank"
        rel="noopener noreferrer"
      >
        <>
          <AppleLogo />
          <span className="btn-text-full">
            {size === 'small' ? 'Download' : 'Download for macOS'}
          </span>
          <span className="btn-text-mobile">Download</span>
        </>
      </a>
    );
  }

  if (platform === 'windows') {
    return (
      <a
        href="/downloads/SoulThread-win.exe"
        className={btnClass}
        target="_blank"
        rel="noopener noreferrer"
      >
        <>
          <WindowsLogo />
          <span className="btn-text-full">
            {size === 'small' ? 'Download' : 'Download for Windows'}
          </span>
          <span className="btn-text-mobile">Download</span>
        </>
      </a>
    );
  }

  if (platform === 'linux') {
    return (
      <a
        href="/downloads/SoulThread-linux.AppImage"
        className={btnClass}
        target="_blank"
        rel="noopener noreferrer"
      >
        <>
          <LinuxLogo />
          <span className="btn-text-full">
            {size === 'small' ? 'Download' : 'Download for Linux'}
          </span>
          <span className="btn-text-mobile">Download</span>
        </>
      </a>
    );
  }

  // Fallback - generic download
  return (
    <a href="/downloads" className={btnClass}>
      Download
    </a>
  );
};

// Hook to get responsive logo size
const useResponsiveLogoSize = () => {
  const [logoSize, setLogoSize] = React.useState(300);

  React.useEffect(() => {
    const updateSize = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        setLogoSize(Math.min(240, width - 80)); // Smaller text for iPhone SE
      } else if (width <= 768) {
        setLogoSize(Math.min(320, width - 60)); // Responsive with padding
      } else {
        setLogoSize(300); // Desktop size
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return logoSize;
};

// Marketing/Landing Page
const HomePage = ({ detectedPlatform }: { detectedPlatform: string }) => {
  const logoSize = useResponsiveLogoSize();

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-background-light">
      <div className="relative min-h-screen flex items-center justify-center px-4 py-20">
        <div className="text-center max-w-4xl mx-auto">
          <div className="mb-8 flex justify-center">
            <HeroLogo size={logoSize} className="mb-8" />
          </div>
          <p className="text-lg md:text-xl text-secondary-purple mb-12 max-w-2xl mx-auto">
            Analyze your messages. Predict quiz results. Understand your personality like never
            before.
          </p>

          <div className="flex justify-center">
            <PlatformDownloadButton platform={detectedPlatform} />
          </div>
        </div>
      </div>

      <div className="py-20 px-4">
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 bg-gradient-to-r from-secondary-cyan via-secondary-pink to-secondary-purple bg-clip-text text-transparent">
          Get To Know the Real You
        </h2>
        <p className="text-center text-secondary-purple text-lg mb-12 max-w-2xl mx-auto">
          Unlock the depth of your personality with advanced AI analysis
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="bg-surface/50 backdrop-blur border border-border rounded-2xl p-8 hover:border-primary transition-all hover:transform hover:scale-105">
            <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center">
              <svg
                className="w-full h-full"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="24" cy="24" r="20" fill="url(#brain-gradient)" opacity="0.2" />
                <circle
                  cx="24"
                  cy="24"
                  r="6"
                  stroke="url(#brain-gradient)"
                  strokeWidth="2"
                  fill="none"
                />
                <circle
                  cx="12"
                  cy="16"
                  r="3"
                  stroke="url(#brain-gradient)"
                  strokeWidth="2"
                  fill="none"
                />
                <circle
                  cx="36"
                  cy="16"
                  r="3"
                  stroke="url(#brain-gradient)"
                  strokeWidth="2"
                  fill="none"
                />
                <circle
                  cx="12"
                  cy="32"
                  r="3"
                  stroke="url(#brain-gradient)"
                  strokeWidth="2"
                  fill="none"
                />
                <circle
                  cx="36"
                  cy="32"
                  r="3"
                  stroke="url(#brain-gradient)"
                  strokeWidth="2"
                  fill="none"
                />
                <path
                  d="M18 24L15 16M30 24L33 16M18 24L15 32M30 24L33 32"
                  stroke="url(#brain-gradient)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="brain-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#67e8f9" />
                    <stop offset="100%" stopColor="#a78bfa" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <h3>Multi-Model AI Analysis</h3>
            <p>
              State-of-the-art machine learning using the best models for each personality dimension
            </p>
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-secondary-cyan to-secondary-purple text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
              Next-Gen AI
            </div>
          </div>
          <div className="relative bg-surface-dark rounded-2xl p-8 shadow-xl border border-gray-800 hover:border-secondary-cyan transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
            <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center">
              <svg
                className="w-full h-full"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="24" cy="24" r="20" fill="url(#lock-gradient)" opacity="0.2" />
                <rect
                  x="16"
                  y="22"
                  width="16"
                  height="12"
                  rx="2"
                  stroke="url(#lock-gradient)"
                  strokeWidth="2"
                />
                <path
                  d="M20 22V18C20 15.8 21.8 14 24 14C26.2 14 28 15.8 28 18V22"
                  stroke="url(#lock-gradient)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle cx="24" cy="28" r="1.5" fill="url(#lock-gradient)" />
                <defs>
                  <linearGradient id="lock-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#67e8f9" />
                    <stop offset="100%" stopColor="#f9a8d4" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <h3 className="text-2xl font-bold mb-3 text-white">Privacy-Focused Design</h3>
            <p className="text-gray-300 leading-relaxed">
              Local processing when possible. Minimal data collection. You control your information.
            </p>
            <div className="inline-block mt-4 px-3 py-1 bg-primary/20 border border-primary rounded-full text-sm text-primary">
              Privacy First
            </div>
          </div>
          <div className="bg-surface/50 backdrop-blur border border-border rounded-2xl p-8 hover:border-primary transition-all hover:transform hover:scale-105">
            <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center">
              <svg
                className="w-full h-full"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="24" cy="24" r="20" fill="url(#personality-gradient)" opacity="0.1" />
                {/* Cleaner concentric layers representing personality depth */}
                <circle
                  cx="24"
                  cy="24"
                  r="16"
                  stroke="url(#personality-gradient)"
                  strokeWidth="1.5"
                  fill="none"
                  opacity="0.2"
                />
                <circle
                  cx="24"
                  cy="24"
                  r="12"
                  stroke="url(#personality-gradient)"
                  strokeWidth="1.5"
                  fill="none"
                  opacity="0.4"
                />
                <circle
                  cx="24"
                  cy="24"
                  r="8"
                  stroke="url(#personality-gradient)"
                  strokeWidth="2"
                  fill="none"
                  opacity="0.6"
                />
                {/* Core personality center */}
                <circle cx="24" cy="24" r="4" fill="url(#personality-gradient)" opacity="0.8" />
                {/* Cleaner trait indicators in cardinal and ordinal directions */}
                <circle cx="24" cy="8" r="1.5" fill="url(#personality-gradient)" opacity="0.9" />
                <circle
                  cx="34.5"
                  cy="13.5"
                  r="1.5"
                  fill="url(#personality-gradient)"
                  opacity="0.9"
                />
                <circle cx="40" cy="24" r="1.5" fill="url(#personality-gradient)" opacity="0.9" />
                <circle
                  cx="34.5"
                  cy="34.5"
                  r="1.5"
                  fill="url(#personality-gradient)"
                  opacity="0.9"
                />
                <circle cx="24" cy="40" r="1.5" fill="url(#personality-gradient)" opacity="0.9" />
                <circle
                  cx="13.5"
                  cy="34.5"
                  r="1.5"
                  fill="url(#personality-gradient)"
                  opacity="0.9"
                />
                <circle cx="8" cy="24" r="1.5" fill="url(#personality-gradient)" opacity="0.9" />
                <circle
                  cx="13.5"
                  cy="13.5"
                  r="1.5"
                  fill="url(#personality-gradient)"
                  opacity="0.9"
                />
                {/* Subtle connecting lines from center to key traits */}
                <path
                  d="M24 20L24 12M28 28L32 32M28 20L32 16M20 28L16 32M20 20L16 16M24 28L24 36M28 24L36 24M20 24L12 24"
                  stroke="url(#personality-gradient)"
                  strokeWidth="1"
                  opacity="0.3"
                />
                <defs>
                  <linearGradient id="personality-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#f9a8d4" />
                    <stop offset="100%" stopColor="#a78bfa" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <h3>100+ Personality Tests</h3>
            <p>
              Complete coverage from MBTI and Big 5 to Enneagram, Love Languages, and viral quizzes
            </p>
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-secondary-cyan to-secondary-purple text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
              Most Comprehensive
            </div>
          </div>
        </div>
      </div>

      <div className="py-20 px-4 bg-background-light/30">
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 bg-gradient-to-r from-secondary-cyan via-secondary-pink to-secondary-purple bg-clip-text text-transparent">
          How It Works
        </h2>
        <p className="text-center text-secondary-purple text-lg mb-12 max-w-2xl mx-auto">
          Three simple steps to discover your authentic self
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-background/50 to-surface/60 rounded-3xl p-8 backdrop-blur-md border border-secondary-cyan/20 text-center relative hover:border-secondary-cyan/40 transition-all">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-secondary-cyan to-secondary-purple flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-background">
              1
            </div>

            <div
              style={{
                width: '140px',
                height: '140px',
                margin: '0 auto 1.5rem',
                padding: '1rem',
                background: 'rgba(5, 150, 105, 0.1)',
                borderRadius: '1rem',
                border: '1px solid rgba(5, 150, 105, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: '100%', height: '100%' }}
              >
                <rect
                  x="12"
                  y="8"
                  width="24"
                  height="32"
                  rx="3"
                  stroke="#059669"
                  strokeWidth="2.5"
                  fill="none"
                />
                <path
                  d="M24 18L24 30M24 30L19 25M24 30L29 25"
                  stroke="#059669"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="24" cy="36" r="2" fill="#059669" />
              </svg>
            </div>

            <h3
              style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                marginBottom: '0.75rem',
                color: '#e2e8f0',
              }}
            >
              Download SoulThread
            </h3>
            <p style={{ fontSize: '0.95rem', color: '#94a3b8', lineHeight: '1.5', margin: 0 }}>
              Desktop app for Mac & Windows
              <br />
              <span style={{ opacity: 0.8 }}>Mobile coming soon</span>
            </p>
          </div>

          <div
            className="step-card"
            style={{
              background:
                'linear-gradient(135deg, rgba(26, 15, 46, 0.5) 0%, rgba(30, 20, 50, 0.6) 100%)',
              borderRadius: '1.5rem',
              padding: '2rem',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(249, 168, 212, 0.2)',
              position: 'relative',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f9a8d4 0%, #67e8f9 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#1a0f2e',
              }}
            >
              2
            </div>

            <div
              style={{
                width: '140px',
                height: '140px',
                margin: '0 auto 1.5rem',
                padding: '1rem',
                background: 'rgba(103, 232, 249, 0.1)',
                borderRadius: '1rem',
                border: '1px solid rgba(103, 232, 249, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: '100%', height: '100%' }}
              >
                <path
                  d="M8 15C8 13.9 8.9 13 10 13L22 13C23.1 13 24 13.9 24 15L24 23C24 24.1 23.1 25 22 25L12 25L8 29L8 15Z"
                  stroke="#67e8f9"
                  strokeWidth="2.5"
                  fill="none"
                />
                <path
                  d="M24 19C24 17.9 24.9 17 26 17L38 17C39.1 17 40 17.9 40 19L40 27C40 28.1 39.1 29 38 29L36 29L40 33L40 19Z"
                  stroke="#67e8f9"
                  strokeWidth="2.5"
                  fill="none"
                />
                <circle cx="13" cy="19" r="1.5" fill="#67e8f9" />
                <circle cx="16" cy="19" r="1.5" fill="#67e8f9" />
                <circle cx="19" cy="19" r="1.5" fill="#67e8f9" />
                <circle cx="29" cy="23" r="1.5" fill="#67e8f9" />
                <circle cx="32" cy="23" r="1.5" fill="#67e8f9" />
                <circle cx="35" cy="23" r="1.5" fill="#67e8f9" />
                <path
                  d="M16 35L32 35"
                  stroke="#67e8f9"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray="3 2"
                />
              </svg>
            </div>

            <h3
              style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                marginBottom: '0.75rem',
                color: '#e2e8f0',
              }}
            >
              Import Your Messages
            </h3>
            <p style={{ fontSize: '0.95rem', color: '#94a3b8', lineHeight: '1.5', margin: 0 }}>
              Secure local processing
              <br />
              <span style={{ opacity: 0.8 }}>iMessage, WhatsApp & more</span>
            </p>
          </div>

          <div
            className="step-card"
            style={{
              background:
                'linear-gradient(135deg, rgba(26, 15, 46, 0.5) 0%, rgba(30, 20, 50, 0.6) 100%)',
              borderRadius: '1.5rem',
              padding: '2rem',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(167, 139, 250, 0.2)',
              position: 'relative',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #a78bfa 0%, #f9a8d4 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#1a0f2e',
              }}
            >
              3
            </div>

            <div
              style={{
                width: '140px',
                height: '140px',
                margin: '0 auto 1.5rem',
                padding: '1rem',
                background: 'rgba(167, 139, 250, 0.1)',
                borderRadius: '1rem',
                border: '1px solid rgba(167, 139, 250, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: '100%', height: '100%' }}
              >
                <circle cx="24" cy="22" r="8" stroke="#a78bfa" strokeWidth="2" fill="none" />
                <path
                  d="M24 30C24 30 16 32 16 36L32 36C32 32 24 30 24 30Z"
                  stroke="#a78bfa"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle cx="21" cy="20" r="1.5" fill="#a78bfa" />
                <circle cx="27" cy="20" r="1.5" fill="#a78bfa" />
                <path
                  d="M20 24C20 24 21 26 24 26C27 26 28 24 28 24"
                  stroke="#a78bfa"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <circle cx="10" cy="12" r="1.5" fill="#a78bfa" opacity="0.6" />
                <circle cx="38" cy="12" r="1.5" fill="#a78bfa" opacity="0.6" />
                <circle cx="38" cy="32" r="1.5" fill="#a78bfa" opacity="0.6" />
                <circle cx="10" cy="32" r="1.5" fill="#a78bfa" opacity="0.6" />
              </svg>
            </div>

            <h3
              style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                marginBottom: '0.75rem',
                color: '#e2e8f0',
              }}
            >
              Get AI Insights
            </h3>
            <p style={{ fontSize: '0.95rem', color: '#94a3b8', lineHeight: '1.5', margin: 0 }}>
              Discover personality patterns
              <br />
              <span style={{ opacity: 0.8 }}>from your conversations</span>
            </p>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: '2rem',
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginTop: '3rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem 1.5rem',
              background: 'rgba(26, 15, 46, 0.4)',
              border: '1px solid rgba(103, 232, 249, 0.2)',
              borderRadius: '1rem',
              backdropFilter: 'blur(5px)',
            }}
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              style={{ width: '20px', height: '20px', flexShrink: 0 }}
            >
              <path
                d="M10 2L3 5V10C3 14.5 6 18.16 10 19C14 18.16 17 14.5 17 10V5L10 2Z"
                stroke="#67e8f9"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7 10L9 12L13 8"
                stroke="#67e8f9"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#e2e8f0' }}>
                Privacy First
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Local processing priority</div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem 1.5rem',
              background: 'rgba(26, 15, 46, 0.4)',
              border: '1px solid rgba(249, 168, 212, 0.2)',
              borderRadius: '1rem',
              backdropFilter: 'blur(5px)',
            }}
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              style={{ width: '20px', height: '20px', flexShrink: 0 }}
            >
              <rect x="4" y="6" width="12" height="10" rx="2" stroke="#f9a8d4" strokeWidth="1.5" />
              <path
                d="M8 6V5C8 3.9 8.9 3 10 3C11.1 3 12 3.9 12 5V6"
                stroke="#f9a8d4"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <circle cx="10" cy="11" r="1" fill="#f9a8d4" />
            </svg>
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#e2e8f0' }}>
                Secure Storage
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>End-to-end encryption</div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem 1.5rem',
              background: 'rgba(26, 15, 46, 0.4)',
              border: '1px solid rgba(167, 139, 250, 0.2)',
              borderRadius: '1rem',
              backdropFilter: 'blur(5px)',
            }}
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              style={{ width: '20px', height: '20px', flexShrink: 0 }}
            >
              <path
                d="M10 10C12.2091 10 14 8.20914 14 6C14 3.79086 12.2091 2 10 2C7.79086 2 6 3.79086 6 6C6 8.20914 7.79086 10 10 10Z"
                stroke="#a78bfa"
                strokeWidth="1.5"
              />
              <path
                d="M10 12C6 12 3 14 3 17V18H17V17C17 14 14 12 10 12Z"
                stroke="#a78bfa"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#e2e8f0' }}>
                You Own Your Data
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Delete anytime</div>
            </div>
          </div>
        </div>
      </div>

      <div className="py-20 px-4 text-center bg-gradient-to-b from-surface to-surface-dark">
        <h2>Ready to Follow Your SoulThread?</h2>
        <p>Join thousands discovering their authentic personality</p>
        <PlatformDownloadButton platform={detectedPlatform} />
      </div>
    </div>
  );
};

// Privacy Policy Page
const PrivacyPage = () => (
  <div className="min-h-screen py-20 px-4">
    <div className="max-w-4xl mx-auto">
      <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-secondary-cyan to-secondary-purple bg-clip-text text-transparent">
        Privacy Policy
      </h1>
      <div className="prose prose-lg prose-invert max-w-none">
        <h2>Our Commitment to You</h2>
        <p>
          At SoulThread, we make your privacy a top priority, and protecting your information
          remains our primary value.
        </p>

        <h2>What We Collect</h2>
        <ul>
          <li>Messages and conversations you choose to import</li>
          <li>Personality quiz results and insights</li>
          <li>Basic account information</li>
        </ul>

        <h2>How We Use Your Data</h2>
        <p>Your data is used exclusively to:</p>
        <ul>
          <li>Generate personality insights</li>
          <li>Predict quiz results</li>
          <li>Improve our analysis algorithms</li>
        </ul>

        <h2>Data Security</h2>
        <p>
          All data is encrypted both in transit and at rest. We use industry-standard security
          measures to protect your information.
        </p>

        <h2>Your Rights</h2>
        <p>You have the right to:</p>
        <ul>
          <li>Access your data</li>
          <li>Request deletion of your data</li>
          <li>Export your data</li>
          <li>Opt-out of any data processing</li>
        </ul>

        <h2>Contact Us</h2>
        <p>
          If you have any questions about our privacy practices, please contact us at
          privacy@soulthread.me
        </p>
      </div>
    </div>
  </div>
);

// Terms Page
const TermsPage = () => (
  <div className="min-h-screen py-20 px-4">
    <div className="max-w-4xl mx-auto">
      <h1>Terms & Conditions</h1>
      <div className="prose prose-lg prose-invert max-w-none">
        <h2>Acceptance of Terms</h2>
        <p>
          By using SoulThread, you agree to these terms and conditions. If you do not agree, please
          do not use our service.
        </p>

        <h2>Use of Service</h2>
        <p>SoulThread provides personality analysis based on your message history. You agree to:</p>
        <ul>
          <li>Provide accurate information</li>
          <li>Use the service for personal, non-commercial purposes</li>
          <li>Not attempt to reverse engineer our algorithms</li>
          <li>Respect the privacy of others</li>
        </ul>

        <h2>Intellectual Property</h2>
        <p>
          All content, features, and functionality of SoulThread are owned by SoulThread, Inc. and
          are protected by international copyright, trademark, and other intellectual property laws.
        </p>

        <h2>Limitation of Liability</h2>
        <p>
          SoulThread is provided "as is" without warranties of any kind. We are not liable for any
          indirect, incidental, special, or consequential damages.
        </p>

        <h2>Changes to Terms</h2>
        <p>
          We reserve the right to modify these terms at any time. Continued use of the service after
          changes constitutes acceptance of the new terms.
        </p>

        <h2>Contact</h2>
        <p>For questions about these terms, contact us at legal@soulthread.me</p>
      </div>
    </div>
  </div>
);

// Contact Page
const ContactPage = () => (
  <div className="min-h-screen py-20 px-4">
    <div className="max-w-4xl mx-auto">
      <h1>Contact Us</h1>
      <div className="prose prose-lg prose-invert max-w-none">
        <p>We'd love to hear from you! Reach out through any of the following channels:</p>

        <div className="grid md:grid-cols-3 gap-8 mt-8">
          <div className="bg-surface-dark rounded-lg p-6 text-center">
            <h3>General Inquiries</h3>
            <p>hello@soulthread.me</p>
          </div>
          <div className="bg-surface-dark rounded-lg p-6 text-center">
            <h3>Support</h3>
            <p>support@soulthread.me</p>
          </div>
          <div className="bg-surface-dark rounded-lg p-6 text-center">
            <h3>Bug Reports</h3>
            <p>Report issues to support@soulthread.me</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

function App() {
  const [detectedPlatform, setDetectedPlatform] = React.useState<
    'ios' | 'android' | 'mac' | 'windows' | 'linux' | 'unknown'
  >('unknown');
  const [showNavbar, setShowNavbar] = React.useState(false);

  React.useEffect(() => {
    // Detect OS and device type
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform.toLowerCase();

    // Detect specific platform
    if (/iphone|ipad|ipod/i.test(userAgent)) {
      setDetectedPlatform('ios');
    } else if (/android/i.test(userAgent)) {
      setDetectedPlatform('android');
    } else if (platform.includes('mac') || /macintosh|mac os x/i.test(userAgent)) {
      setDetectedPlatform('mac');
    } else if (platform.includes('win') || /windows|win32/i.test(userAgent)) {
      setDetectedPlatform('windows');
    } else if (platform.includes('linux') || /linux/i.test(userAgent)) {
      setDetectedPlatform('linux');
    }

    // Smooth scroll to hash
    if (window.location.hash === '#app-downloads') {
      const element = document.querySelector('.app-downloads');
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  React.useEffect(() => {
    const handleScroll = () => {
      const heroContent = document.querySelector('.hero-content');
      if (heroContent) {
        const rect = heroContent.getBoundingClientRect();
        const heroHeight = rect.height;
        const heroTop = rect.top;
        // Show navbar when the top of the screen is past the halfway point of hero-content
        const shouldShow = heroTop < -(heroHeight / 2);
        setShowNavbar(shouldShow);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <nav
        className={`fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border/50 transition-transform duration-300 ${showNavbar ? 'translate-y-0' : '-translate-y-full'}`}
      >
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <NavBarLogo />
          <div className="flex items-center gap-4">
            <PlatformDownloadButton platform={detectedPlatform} size="small" />
          </div>
        </div>
      </nav>

      <main className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage detectedPlatform={detectedPlatform} />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
      </main>

      <footer className="bg-background-light/50 border-t border-border/50 py-6 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} SoulThread, Inc. All rights reserved.
          </div>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/privacy" className="text-gray-400 hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            <span className="text-gray-600">•</span>
            <Link to="/terms" className="text-gray-400 hover:text-primary transition-colors">
              Terms & Conditions
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
